import React from 'react';
import { MealSlotCard } from './MealSlotCard';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

interface DayPlanCardProps {
  day: any;
  dayName: string;
  planId: number;
  onSwapMeal: (entry: any) => void;
  onLockMeal: (entryId: number) => void;
  onRegenerateDay: () => void;
  isRegenerating?: boolean;
}

export const DayPlanCard: React.FC<DayPlanCardProps> = ({
  day,
  dayName,
  planId,
  onSwapMeal,
  onLockMeal,
  onRegenerateDay,
  isRegenerating,
}) => {
  const entries = day.entries || [];
  const dayNutrition = day.daily_nutrition || {};

  // Group entries by meal type for display
  const mealTypeOrder = ['breakfast', 'morning_snack', 'lunch', 'afternoon_snack', 'dinner', 'evening_snack'];
  const sortedEntries = [...entries].sort((a, b) => {
    return mealTypeOrder.indexOf(a.meal_type) - mealTypeOrder.indexOf(b.meal_type);
  });

  const formatMealType = (type: string) => {
    const labels: Record<string, string> = {
      breakfast: 'Breakfast',
      morning_snack: 'Morning Snack',
      lunch: 'Lunch',
      afternoon_snack: 'Afternoon Snack',
      dinner: 'Dinner',
      evening_snack: 'Evening Snack',
    };
    return labels[type] || type;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Day Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-700 px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-white">{dayName}</h3>
            <p className="text-xs text-blue-100">{formatDate(day.day_date)}</p>
          </div>
          <button
            onClick={onRegenerateDay}
            disabled={isRegenerating}
            className="p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
            title="Regenerate day (keeps locked meals)"
          >
            <ArrowPathIcon className={`h-4 w-4 text-white ${isRegenerating ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Daily Nutrition Summary */}
      <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
        <div className="flex justify-between text-xs text-gray-600">
          <span>{Math.round(dayNutrition.calories || 0)} cal</span>
          <span>{Math.round(dayNutrition.protein || 0)}g P</span>
          <span>{Math.round(dayNutrition.carbs || 0)}g C</span>
          <span>{Math.round(dayNutrition.fat || 0)}g F</span>
        </div>
      </div>

      {/* Meal Entries */}
      <div className="divide-y divide-gray-100">
        {sortedEntries.length === 0 ? (
          <div className="px-4 py-6 text-center text-gray-400 text-sm">
            No meals planned
          </div>
        ) : (
          sortedEntries.map((entry: any) => (
            <MealSlotCard
              key={entry.id}
              entry={entry}
              mealTypeLabel={formatMealType(entry.meal_type)}
              onSwap={() => onSwapMeal(entry)}
              onLock={() => onLockMeal(entry.id)}
            />
          ))
        )}
      </div>

      {/* Day Notes */}
      {day.notes && (
        <div className="px-4 py-3 bg-blue-50 border-t border-blue-100">
          <p className="text-xs text-blue-700">{day.notes}</p>
        </div>
      )}

      {/* Educational Links */}
      {day.educational_links && day.educational_links.length > 0 && (
        <div className="px-4 py-2 bg-teal-50 border-t border-teal-100">
          <p className="text-xs text-teal-600">
            Learn more: {day.educational_links.slice(0, 2).map((link: any) => link.title).join(', ')}
          </p>
        </div>
      )}
    </div>
  );
};
